import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";

interface BulkStatementOfAccountModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
}

/**
 * Statement of Account (bulk) — mirrors the legacy minimal modal: a single Print
 * action + Close. Print generates each selected student's fee statement (reusing
 * the existing per-enrollment SOA HTML endpoint), concatenates them with a
 * page-break, and opens the browser print dialog — exactly like legacy.
 */
const BulkStatementOfAccountModal: React.FC<BulkStatementOfAccountModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
}) => {
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  // All enrollments under the selected students (legacy prints every selected one).
  const enrollmentIds = useMemo(() => {
    const ids: number[] = [];
    for (const eqId of selectedEnquiryIds) {
      for (const enr of enquiryEnrollmentsMap[eqId] || []) {
        if (enr?.enrollmentId) ids.push(enr.enrollmentId);
      }
    }
    return ids;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap]);

  const handlePrint = async () => {
    setError("");
    if (enrollmentIds.length === 0) {
      setError("The selected students have no enrollments.");
      return;
    }
    // Open the print window synchronously (avoids the async popup blocker), then
    // fill it once the statements are fetched.
    const w = window.open("", "_blank");
    if (w) {
      w.document.write("<p style='font-family:sans-serif;padding:16px'>Generating statement of account…</p>");
    }
    setPrinting(true);
    try {
      const responses = await Promise.all(
        enrollmentIds.map((id) => EnrollmentService.getSOA(id)),
      );
      const htmls = responses
        .map((r) => r?.data?.html || "")
        .filter((h) => h);
      if (htmls.length === 0) {
        setError("No statement of account could be generated for the selected students.");
        if (w) w.close();
        return;
      }
      const combined = htmls.join('<p style="page-break-after:always;"></p>');
      if (w) {
        w.document.open();
        w.document.write(combined);
        w.document.close();
        w.focus();
        w.print();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to generate statement of account");
      if (w) w.close();
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Statement Of Account</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            border: "1px solid var(--border-default)",
            background: "var(--surface-page)",
            borderRadius: 8,
            padding: "40px 20px",
            textAlign: "center",
          }}
        >
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing || enrollmentIds.length === 0}
            style={{
              background: "none",
              border: "none",
              cursor: printing || enrollmentIds.length === 0 ? "default" : "pointer",
              color: "var(--brand-primary)",
            }}
          >
            <div>
              {printing ? (
                <i className="fa fa-spinner fa-spin" style={{ fontSize: 56 }} />
              ) : (
                <i className="fa fa-print" style={{ fontSize: 56 }} />
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 15 }}>
              {printing ? "Generating…" : "Print"}
            </div>
          </button>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={printing}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkStatementOfAccountModal;
